import { useState } from 'react';
import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { IconRefresh } from '@shared/ui/icons';
import { apiErrorText, useRefreshCounterpartyExternalMutation } from '@shared/api';
import type { Counterparty, FsspFlag, RnpFlag } from '@entities/legal/types';

// — блок «Внешние реестры» в карточке контрагента.
// Отображает результаты двух проверок: РНП (ФАС) и ФССП.
// Источник данных — `counterparty.risk_flags.rnp` и `.fssp`, заполняются
// вызовом /counterparties/{id}/refresh-external.

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  line-height: 1.45;
`;

const RowMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2px;
`;

const RowHint = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

const SourceLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

function fmtDt(value?: string): string {
  if (!value) return 'не проверялось';
  return new Date(value).toLocaleString('ru-RU');
}

interface Props {
  counterparty: Counterparty;
}

export function ExternalRegistries({ counterparty }: Props) {
  const [refresh, { isLoading }] = useRefreshCounterpartyExternalMutation();
  const [error, setError] = useState<string | null>(null);

  const flags = (counterparty.risk_flags ?? {}) as Record<string, unknown>;
  const rnp = (flags.rnp ?? null) as RnpFlag | null;
  const fssp = (flags.fssp ?? null) as FsspFlag | null;
  const hasAnyCheck = rnp != null || fssp != null;

  async function onRefresh() {
    setError(null);
    try {
      await refresh(counterparty.id).unwrap();
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось выполнить проверку'));
    }
  }

  return (
    <Wrap>
      <Toolbar>
        <span style={{ fontSize: 12, color: 'var(--osnova-text-muted, #888)' }}>
          . Проверка контрагента в публичных федеральных реестрах (ФАС, ФССП). Используется при
          заключении договора как due-diligence.
        </span>
        <Button
          type="button"
          $size="sm"
          $variant="secondary"
          onClick={onRefresh}
          disabled={isLoading}
          title="Параллельная проверка РНП и ФССП по ИНН"
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <IconRefresh size={14} />
            {isLoading ? 'Проверяем…' : hasAnyCheck ? 'Обновить' : 'Проверить'}
          </span>
        </Button>
      </Toolbar>

      {error && (
        <div
          style={{
            color: 'var(--osnova-danger, #d04848)',
            fontSize: 12,
            padding: 8,
            background: 'rgba(208, 72, 72, 0.08)',
            borderRadius: 6,
          }}
        >
          {error}
        </div>
      )}

      <Row>
        <RowMain>
          <RowName>Реестр недобросовестных поставщиков (ФАС)</RowName>
          {rnp ? (
            rnp.error ? (
              <RowHint style={{ color: 'var(--osnova-warning, #d99c2a)' }}>
                Проверка не выполнена: {rnp.error}
              </RowHint>
            ) : (
              <>
                <RowHint>
                  Проверено: {fmtDt(rnp.checked_at)}
                  {rnp.is_listed
                    ? ` · найдено записей: ${rnp.records_count ?? 0}`
                    : ' · записей нет'}
                </RowHint>
                {rnp.source_url && (
                  <SourceLink href={rnp.source_url} target="_blank" rel="noopener noreferrer">
                    Открыть на zakupki.gov.ru →
                  </SourceLink>
                )}
              </>
            )
          ) : (
            <RowHint>Проверка не выполнялась.</RowHint>
          )}
        </RowMain>
        <div>
          {rnp == null ? (
            <Badge>—</Badge>
          ) : rnp.error ? (
            <Badge $tone="warning">Недоступно</Badge>
          ) : rnp.is_listed ? (
            <Badge $tone="danger">В реестре</Badge>
          ) : (
            <Badge $tone="success">Не найден</Badge>
          )}
        </div>
      </Row>

      <Row>
        <RowMain>
          <RowName>ФССП — исполнительные производства</RowName>
          {fssp ? (
            fssp.error ? (
              <RowHint style={{ color: 'var(--osnova-warning, #d99c2a)' }}>{fssp.error}</RowHint>
            ) : fssp.source === 'unavailable' ? (
              <RowHint>
                Проверено: {fmtDt(fssp.checked_at)}. Сервис ФССП требует регистрации
                (api-ip.fssp.gov.ru), ключ не подключён.
              </RowHint>
            ) : (
              <RowHint>
                Проверено: {fmtDt(fssp.checked_at)} · открытых производств:{' '}
                {fssp.open_executions ?? 0}
                {fssp.total_debt_rub != null &&
                  ` · сумма ${fssp.total_debt_rub.toLocaleString('ru-RU')} ₽`}
              </RowHint>
            )
          ) : (
            <RowHint>Проверка не выполнялась. Требуется FSSP_API_KEY и согласие ФССП.</RowHint>
          )}
        </RowMain>
        <div>
          {fssp == null ? (
            <Badge>—</Badge>
          ) : fssp.error || fssp.source === 'unavailable' ? (
            <Badge $tone="warning">Требуется ключ</Badge>
          ) : (fssp.open_executions ?? 0) > 0 ? (
            <Badge $tone="danger">{fssp.open_executions}</Badge>
          ) : (
            <Badge $tone="success">Нет</Badge>
          )}
        </div>
      </Row>
    </Wrap>
  );
}
